import { b as _extends, a as _objectSpread, c as _objectWithoutPropertiesLoose } from './chunk-fb907809.js';
import React__default from 'react';
import PropTypes__default from 'prop-types';
import { Container } from 'constate';
import { theme } from 'styled-tools';
import { a as callAll } from './chunk-d8771a86.js';
import styled from './styled.js';
import as from './as.js';
import Box from './Box.js';

var omit = function omit(target, undesired) {
  if (undesired === void 0) {
    undesired = [];
  }

  var targetKeys = Object.keys(target);
  var result = {};

  for (var index = 0; index < targetKeys.length; index += 1) {
    var key = targetKeys[index];
    if (undesired.indexOf(key) === -1) result[key] = target[key];
  }

  return result;
};

var initialState = {
  loop: false,
  ids: [],
  current: -1,
  ordered: {}
};
var getCurrentId = function getCurrentId() {
  return function (state) {
    return state.ids[state.current];
  };
};
var hasPrevious = function hasPrevious() {
  return function (state) {
    return state.ids.length > 1 && !!state.ids[state.current - 1];
  };
};
var hasNext = function hasNext() {
  return function (state) {
    return state.ids.length > 1 && !!state.ids[state.current + 1];
  };
};
var indexOf = function indexOf(idOrIndex) {
  return function (state) {
    return typeof idOrIndex === "number" ? idOrIndex : state.ids.indexOf(idOrIndex);
  };
};
var isCurrent = function isCurrent(idOrIndex) {
  return function (state) {
    return state.current >= 0 && state.current === indexOf(idOrIndex)(state);
  };
};
var show = function show(idOrIndex) {
  return function (state) {
    return {
      current: indexOf(idOrIndex)(state)
    };
  };
};
var hide = function hide() {
  return function () {
    return {
      current: -1
    };
  };
};
var toggle = function toggle(idOrIndex) {
  return function (state) {
    return isCurrent(idOrIndex)(state) ? hide()() : show(idOrIndex)(state);
  };
};
var previous = function previous() {
  return function (state) {
    if (hasPrevious()(state)) {
      return {
        current: state.current - 1
      };
    }

    if (state.loop) {
      return {
        current: state.ids.length - 1
      };
    }

    return {};
  };
};
var next = function next() {
  return function (state) {
    if (hasNext()(state)) {
      return {
        current: state.current + 1
      };
    }

    if (state.loop) {
      return {
        current: 0
      };
    }

    return {};
  };
};
var reorder = function reorder(id, order) {
  if (order === void 0) {
    order = 0;
  }

  return function (state) {
    var _objectSpread2;

    var ordered = _objectSpread({}, state.ordered, (_objectSpread2 = {}, _objectSpread2[id] = order, _objectSpread2));

    var ids = state.ids.slice().sort(function (a, b) {
      return (ordered[a] || 0) - (ordered[b] || 0);
    });
    return _objectSpread({
      ordered: ordered,
      ids: ids
    }, isCurrent(id)(state) ? show(id)(_objectSpread({}, state, {
      ids: ids
    })) : {});
  };
};
var register = function register(id, order) {
  if (order === void 0) {
    order = 0;
  }

  return function (state) {
    var ids = state.ids.indexOf(id) >= 0 ? state.ids : state.ids.concat([id]);
    return reorder(id, order)(_objectSpread({}, state, {
      ids: ids
    }));
  };
};
var unregister = function unregister(id) {
  return function (state) {
    var index = indexOf(id)(state);

    if (index === -1) {
      return {};
    }

    var ordered = omit(state.ordered, state.ids[index]);
    var ids = state.ids.slice(0, index).concat(state.ids.slice(index + 1));

    if (isCurrent(id)(state) && !hasNext()(state)) {
      if (hasPrevious()(state)) {
        return _objectSpread({}, previous()(state), {
          ids: ids,
          ordered: ordered
        });
      }

      return _objectSpread({}, hide()(), {
        ids: ids,
        ordered: ordered
      });
    }

    if (state.current >= ids.length) {
      return {
        current: ids.length - 1,
        ids: ids,
        ordered: ordered
      };
    }

    return {
      ids: ids,
      ordered: ordered
    };
  };
};
var update = function update(id, nextId, orderArg) {
  return function (state) {
    var order = typeof orderArg !== "undefined" ? orderArg : state.ordered[id];
    var idChanged = id !== nextId;
    var orderChanged = order !== state.ordered[id];
    if (!idChanged && !orderChanged) return {};
    var overridingId = idChanged && state.ids.indexOf(nextId) >= 0;

    if (overridingId) {
      var nextOrderChanged = order !== state.ordered[nextId];
      var nextState = nextOrderChanged ? reorder(nextId, order)(state) : {};
      return unregister(id)(_objectSpread({}, state, nextState));
    }

    var index = indexOf(id)(state);
    var ids = state.ids.slice(0, index).concat([nextId], state.ids.slice(index + 1));
    return reorder(nextId, order)(_objectSpread({}, state, {
      ids: ids
    }));
  };
};
var selectors = {
  getCurrentId: getCurrentId,
  hasPrevious: hasPrevious,
  hasNext: hasNext,
  indexOf: indexOf,
  isCurrent: isCurrent
};
var actions = {
  show: show,
  hide: hide,
  toggle: toggle,
  previous: previous,
  next: next,
  reorder: reorder,
  register: register,
  unregister: unregister,
  update: update
}; // istanbul ignore next

var StepContainer = function StepContainer(props) {
  return React__default.createElement(Container, _extends({}, props, {
    initialState: _objectSpread({}, initialState, props.initialState),
    selectors: _objectSpread({}, selectors, props.selectors),
    actions: _objectSpread({}, actions, props.actions)
  }));
};

StepContainer.propTypes = {
  initialState: PropTypes__default.object,
  actions: PropTypes__default.objectOf(PropTypes__default.func),
  selectors: PropTypes__default.objectOf(PropTypes__default.func)
};

var Component = function Component(_ref) {
  var onClick = _ref.onClick,
      props = _objectWithoutPropertiesLoose(_ref, ["onClick"]);

  return React__default.createElement(Box, _extends({
    onClick: callAll(props.next, onClick),
    disabled: !props.loop && props.hasNext && !props.hasNext()
  }, props));
};

var StepNext = styled(Component).withConfig({
  displayName: "StepNext",
  componentId: "sc-1sc449e-0"
})(["", ";"], theme("StepNext"));
StepNext.propTypes = {
  next: PropTypes__default.func.isRequired,
  hasNext: PropTypes__default.func,
  loop: PropTypes__default.bool,
  onClick: PropTypes__default.func
};
var StepNext$1 = as("button")(StepNext);

var Component$1 = function Component(_ref) {
  var onClick = _ref.onClick,
      props = _objectWithoutPropertiesLoose(_ref, ["onClick"]);

  return React__default.createElement(Box, _extends({
    onClick: callAll(props.previous, onClick),
    disabled: !props.loop && props.hasPrevious && !props.hasPrevious()
  }, props));
};

var StepPrevious = styled(Component$1).withConfig({
  displayName: "StepPrevious",
  componentId: "a06c1y-0"
})(["", ";"], theme("StepPrevious"));
StepPrevious.propTypes = {
  previous: PropTypes__default.func.isRequired,
  hasPrevious: PropTypes__default.func,
  loop: PropTypes__default.bool,
  onClick: PropTypes__default.func
};
var StepPrevious$1 = as("button")(StepPrevious);

export { StepContainer as a, StepNext$1 as b, StepPrevious$1 as c };
